import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { getDataSource } from '@/services/conceptsService';

const InfoButtonWrapper = styled.button`
  position: fixed;
  top: 20px;
  right: 20px;
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  border: none;
  font-size: 40px;
  cursor: pointer;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 15px 50px rgba(0, 0, 0, 0.4);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const DrawerOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  cursor: pointer;
`;

const DrawerContent = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  height: 100%;
  width: 400px;
  max-width: 90%;
  background: white;
  box-shadow: -5px 0 30px rgba(0, 0, 0, 0.3);
  z-index: 10000;
  padding: 40px 30px;
  overflow-y: auto;
  cursor: default;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #f0f0f0;
  border: none;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: #e0e0e0;
    transform: rotate(90deg);
  }
`;

const DrawerTitle = styled.h2`
  color: #6B8E23;
  font-size: 28px;
  margin-bottom: 20px;
  margin-top: 10px;
`;

const DrawerText = styled.p`
  color: #333;
  font-size: 16px;
  line-height: 1.8;
  margin-bottom: 20px;
`;

const DrawerSubtitle = styled.h3`
  color: #6B8E23;
  font-size: 20px;
  margin-top: 25px;
  margin-bottom: 15px;
`;

const DrawerList = styled.ul`
  color: #333;
  font-size: 16px;
  line-height: 1.8;
  margin-left: 20px;
  
  li {
    margin-bottom: 10px;
  }
`;

const DataSourceBadge = styled.div<{ source: 'api' | 'local' }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  margin-top: 20px;
  background: ${props => props.source === 'api' 
    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
    : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'};
  color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: white;
    animation: pulse 2s ease-in-out infinite;
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const VersionBadge = styled.div`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: #e5e7eb;
  color: #6b7280;
  margin-top: 10px;
`;

export const InfoButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [dataSource, setDataSource] = useState<'api' | 'local'>('local');

  useEffect(() => {
    // Update data source when drawer opens
    if (isOpen) {
      setDataSource(getDataSource());
    }
  }, [isOpen]);

  return (
    <>
      <InfoButtonWrapper onClick={() => setIsOpen(true)}>
        ❓
      </InfoButtonWrapper>

      <AnimatePresence>
        {isOpen && (
          <>
            <DrawerOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
            />
            <DrawerContent
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            >
              <CloseButton onClick={() => setIsOpen(false)}>
                ✕
              </CloseButton>

              <DrawerTitle>🎈 Estoure a Bolha da IA</DrawerTitle>
              
              <DrawerText>
                Bem-vindo ao <strong>Estoure a Bolha da IA</strong>! Este é um jogo educativo 
                interativo criado para desmistificar conceitos de Inteligência Artificial, 
                Large Language Models (LLMs), Machine Learning e frameworks agênticos de 
                forma divertida e acessível.
              </DrawerText>

              <DrawerSubtitle>Como Jogar</DrawerSubtitle>
              <DrawerList>
                <li>Use o mouse para controlar a agulha</li>
                <li>Clique nos balões para estourá-los</li>
                <li>Aprenda sobre conceitos de IA a cada balão estourado</li>
                <li>Complete todos os 15 balões para vencer o jogo!</li>
              </DrawerList>

              <DrawerSubtitle>Objetivo</DrawerSubtitle>
              <DrawerText>
                Nosso objetivo é tornar o conhecimento sobre IA acessível a todos, 
                "estourando a bolha" de complexidade que muitas vezes envolve esses temas. 
                Cada balão representa um conceito fundamental que você precisa conhecer 
                para entender o mundo da Inteligência Artificial.
              </DrawerText>

              <DataSourceBadge source={dataSource}>
                {dataSource === 'api' ? '🚀 Conceitos sendo carregados do DynamoDB' : '📦 Conceitos sendo carregados localmente (fallback)'}
              </DataSourceBadge>

              <DrawerText style={{ marginTop: '30px', fontSize: '14px', color: '#666' }}>
                Desenvolvido por <strong>Um Dia Bonito</strong>
                <VersionBadge>v1.0.0</VersionBadge>
              </DrawerText>
            </DrawerContent>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
